import { Sliders, Trash2, X } from 'lucide-react';
import type { WorkflowNode, WorkflowNodeData } from './types';

interface NodePropertiesProps {
  node: WorkflowNode | undefined;
  onUpdateData: (nodeId: string, patch: Partial<WorkflowNodeData>) => void;
  onUpdateParam: (nodeId: string, key: string, value: string | number | boolean) => void;
  onDelete: (nodeId: string) => void;
  /** Hide the panel (deselect) without deleting. */
  onClose: () => void;
}

// Floating panel over the canvas (top-right). Shows only while a node is
// selected; onClose (the X) or clicking empty canvas hides it. Edits mutate the
// node's data (label, description, params) — the editor syncs the change back
// into the graph and persists on the debounced autosave. Pure prop-driven.
export function NodeProperties({ node, onUpdateData, onUpdateParam, onDelete, onClose }: NodePropertiesProps) {
  if (!node) return null;
  const d = node.data;

  return (
    <div
      className="absolute right-2 top-2 w-[min(20rem,calc(100%-1rem))] max-h-[calc(100%-1rem)] overflow-y-auto z-20 rounded-xl border p-4 space-y-4 shadow-lg animate-fadeIn md:right-4 md:top-4 md:max-h-[calc(100%-2rem)]"
      style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
      id="canvas-properties-sidebar"
    >
      <div className="flex items-center justify-between border-b pb-2" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-1.5 min-w-0">
          <Sliders className="w-4 h-4 shrink-0" style={{ color: 'var(--accent)' }} />
          <h4 className="text-[11px] font-bold uppercase tracking-wider truncate" style={{ color: 'var(--ink)' }}>Configure Node</h4>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            onClick={() => onDelete(node.id)}
            className="p-1 rounded transition hover:bg-rose-50"
            style={{ color: '#dc2626' }}
            title="Delete node"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded transition"
            style={{ color: 'var(--ink-secondary)' }}
            title="Close (deselect)"
            aria-label="Close panel"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <span className="text-[8px] font-mono font-bold border px-2 py-0.5 rounded uppercase" style={{ color: 'var(--accent)', background: 'var(--accent-bg)', borderColor: 'var(--border)' }}>{d.kind}</span>
        <label className="text-[9px] font-mono font-bold uppercase block" style={{ color: 'var(--ink-secondary)' }}>Label</label>
        <input
          type="text"
          value={d.label}
          onChange={(e) => onUpdateData(node.id, { label: e.target.value })}
          className="w-full border p-1.5 text-xs rounded outline-none"
          style={{ background: 'var(--ink-faint)', borderColor: 'var(--border)', color: 'var(--ink)' }}
        />
        <label className="text-[9px] font-mono font-bold uppercase block" style={{ color: 'var(--ink-secondary)' }}>Description</label>
        <textarea
          value={d.description}
          onChange={(e) => onUpdateData(node.id, { description: e.target.value })}
          rows={2}
          className="w-full border p-1.5 text-[10px] rounded outline-none resize-none"
          style={{ background: 'var(--ink-faint)', borderColor: 'var(--border)', color: 'var(--ink)' }}
        />
      </div>

      <div className="space-y-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
        {Object.keys(d.params).length === 0 ? (
          <p className="text-[10px] italic" style={{ color: 'var(--ink-secondary)' }}>No parameters for this node.</p>
        ) : (
          Object.entries(d.params).map(([key, val]) => (
            <div key={key} className="space-y-1">
              <label className="text-[9px] font-mono font-bold capitalize block" style={{ color: 'var(--ink-secondary)' }}>
                {key.replace(/([A-Z])/g, ' $1')}
              </label>
              {typeof val === 'boolean' ? (
                <select
                  value={val ? 'true' : 'false'}
                  onChange={(e) => onUpdateParam(node.id, key, e.target.value === 'true')}
                  className="w-full border p-1.5 text-xs rounded outline-none font-mono"
                  style={{ background: 'var(--ink-faint)', borderColor: 'var(--border)', color: 'var(--ink)' }}
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              ) : typeof val === 'number' ? (
                <input
                  type="number"
                  value={val}
                  onChange={(e) => onUpdateParam(node.id, key, parseFloat(e.target.value) || 0)}
                  className="w-full border p-1.5 text-xs rounded outline-none font-mono"
                  style={{ background: 'var(--ink-faint)', borderColor: 'var(--border)', color: 'var(--ink)' }}
                />
              ) : (
                <input
                  type="text"
                  value={String(val)}
                  onChange={(e) => onUpdateParam(node.id, key, e.target.value)}
                  className="w-full border p-1.5 text-[10px] rounded outline-none font-mono"
                  style={{ background: 'var(--ink-faint)', borderColor: 'var(--border)', color: 'var(--ink)' }}
                />
              )}
            </div>
          ))
        )}
      </div>

      <div className="border p-2.5 rounded-lg text-[9px] leading-normal" style={{ background: 'var(--ink-faint)', borderColor: 'var(--border)', color: 'var(--ink-secondary)' }}>
        <span className="font-bold block" style={{ color: 'var(--ink)' }}>Autosave:</span> Changes persist to D1 automatically.
      </div>
    </div>
  );
}
